import { div } from '../util/dom_utils';
import { getHitLabel, getHitType, getHitSVG } from './location_search_util';
import { t } from '../messages';

const RESULT_ROW_CLASS = 'location-search__results-row';
const FOCUSED_ROW_CLASS = `${RESULT_ROW_CLASS}--focused`;
const RESULT_ROW_ICON_CLASS = 'location-search__results-row__icon';
const RESULT_ROW_LABEL_CLASS = 'location-search__results-row__label';

export class LocationSearchResults {
  constructor({ container, locale, handleSelect }) {
    this.container = container;
    this.locale = locale;
    this.handleSelect = handleSelect;
    this.focusedIndex = 0;
    this.hits = [];
  }

  updateResults({ query, hits, address, error }) {
    this.hits = hits;
    const { locale } = this;
    this.renderResults({ query, hits, address, locale, error });
    this.setFocusedIndex(0);
  }

  focusNext() {
    let index = this.focusedIndex + 1;
    if (index >= this.hits.length) {
      index = 0;
    }
    this.setFocusedIndex(index);
  }

  focusPrev() {
    let index = this.focusedIndex - 1;
    if (index < 0) {
      index = this.hits.length - 1;
    }
    this.setFocusedIndex(index);
  }

  setFocusedIndex(index) {
    this.focusedIndex = index;
    const rows = this.container.getElementsByClassName(RESULT_ROW_CLASS);
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (index === i) {
        row.classList.add(FOCUSED_ROW_CLASS);
      } else {
        row.classList.remove(FOCUSED_ROW_CLASS);
      }
    }
  }

  select() {
    if (this.focusedIndex < this.hits.length) {
      this.handleSelect(this.hits[this.focusedIndex]);
    }
  }

  reset() {
    this.updateResults({ query: '', hits: [] });
  }

  // private

  renderResults({ hits, query, locale, address, error }) {
    let rootEl;

    if (error) {
      rootEl = renderError();
    } else if (hits.length) {
      rootEl = this.renderHits({ hits, query, locale, address });
    } else {
      rootEl = renderNoHits({ query, locale, address });
    }

    this.container.replaceChild(rootEl, this.container.firstChild);
  }

  renderHits({ hits, query, locale, address }) {
    const container = div();
    container.appendChild(renderSubtitle({ query, address }));

    let index = 0;
    for (let hit of hits) {
      const hitType = getHitType(hit);
      const svg = getHitSVG(hitType);
      const icon = div({ html: svg, className: `${RESULT_ROW_ICON_CLASS} ${RESULT_ROW_ICON_CLASS}--${hitType}` });
      const label = div({ html: getHitLabel(hit, true, locale), className: RESULT_ROW_LABEL_CLASS });
      const row = div(
        {
          className: RESULT_ROW_CLASS,
          events: {
            click: this.handleSelect.bind(null, hit),
            mouseenter: this.setFocusedIndex.bind(this, index)
          }
        },
        [icon, label]
      );
      index++;
      container.appendChild(row);
    }
    return container;
  }
}

// helpers

function renderNoHits({ query, address }) {
  const message = address
    ? t('locationSearch.noResultsWithAddress', { query, address })
    : t('locationSearch.noResults', { query });
  return div({ html: message, className: 'location-search__no-results' });
}

function renderError() {
  const message = t('locationSearch.generalError');
  return div({ html: message, className: 'location-search__no-results' });
}

function renderSubtitle({ query, address }) {
  let message;
  if (!query) {
    message = t('locationSearch.closestHitsHeading');
  } else if (address) {
    message = t('locationSearch.nearbyResultsHeadingWithAddress', { query, address });
  } else {
    message = t('locationSearch.nearbyResultsHeading', { query });
  }

  return div({ text: message, className: 'location-search__results-subtitle' });
}
